import { useEffect, useState } from 'react';
import { InfiniteTable } from '@/components/_common';
import { InfiniteTableProps } from '@/components/_common/InfiniteTable';
import StatusTag from '@/components/_common/StatusTag';
import {
  CrmFutureService,
  RevisionItem,
  RevisionItemType,
} from '@/core/domain/crm/CrmList/CrmList.types';
import { SendOptionType } from '@/core/domain/crm/SendOptions/SendOptions.types';
import {
  AccountReceivableSituation,
  AccountReceivableSituationColor,
  AccountRecivableSituationType,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { useQueryParams } from '@/hooks/useQueryParams';
import { Button, Col, Form, Input, Popover, Row, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';

import './style.less';
import { ExpandableConfig } from 'antd/lib/table/interface';
import { useWindowSize } from '@/hooks';
import {
  workorderTypeColor,
  workorderTypeItemSmall,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';

export interface CrmReceiverCustomer {
  id: number;

  customer: {
    vehicle?: {
      id: number;
      model?: string;
      brand?: string;
      licensePlate?: string;
    };
    name: string;
    id: number;
    email?: string;
    date?: string;
    lastSendedEmail?: string;
  };

  vehicle?: {
    id: number;
    model?: string;
    brand?: string;
    licensePlate?: string;
  };

  debit?: {
    id: number;
    code: string;
    situation: AccountReceivableSituation;
    dueDate: string;
  };

  futureServices?: CrmFutureService[];

  itens?: RevisionItem[];
}

interface Customer {
  customer: { id: number; email: string };
}

const withVehicles = [SendOptionType.custom, SendOptionType.emailMarketing];

interface Props extends InfiniteTableProps<CrmReceiverCustomer> {
  CustomersSelected: CrmReceiverCustomer[];
  onCustomersSelected: React.Dispatch<
    React.SetStateAction<CrmReceiverCustomer[]>
  >;
  customerIds: number[];
  refetch?: VoidFunction;
}

function ShippingOptionsCustomerTable({
  CustomersSelected,
  onCustomersSelected,
  customerIds,
  ...props
}: Props) {
  const { isMobile } = useWindowSize();
  const [selectAll, setSelectAll] = useState(false);
  const [, setInvertSelection] = useState(false);
  const [notSentRecent, setNotSentRecent] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [selectedRowKeysItens, setSelectedRowKeysItens] = useState<
    { id: number; itensIds: number[] }[]
  >([]);

  const { type: shippingType } = useQueryParams<{ type: SendOptionType }>();

  const form = Form.useFormInstance();

  useEffect(() => {
    if (!!customerIds.length && !selectedRowKeys.length) {
      setSelectedRowKeys(customerIds);
    }
  }, [customerIds]);

  useEffect(() => {
    if (!!CustomersSelected.length && !selectedRowKeysItens.length) {
      setSelectedRowKeysItens(
        CustomersSelected.map((item) => ({
          id: item.id,
          itensIds: item.itens?.map((item) => item.id) || [],
        })),
      );
    }
  }, [CustomersSelected]);

  function handleSelectAll() {
    const selectedCustomers =
      props.dataSource?.map((item) => ({ ...item })) || [];

    onCustomersSelected(selectedCustomers);
    setSelectedRowKeys(selectedCustomers.map((item) => item.id) || []);
    setSelectedRowKeysItens(
      selectedCustomers.map((item) => ({
        id: item.id,
        itensIds: item.itens?.map(({ id }) => id) || [],
      })) || [],
    );
    setSelectAll(true);
    setInvertSelection(false);
    setNotSentRecent(false);
  }

  function handleRemoveSelection() {
    onCustomersSelected([]);
    setSelectedRowKeys([]);
    setSelectedRowKeysItens([]);
    setSelectAll(false);
    setInvertSelection(false);
    setNotSentRecent(false);
  }

  function handleInvertSelection() {
    onCustomersSelected(
      (props.dataSource
        ?.map((item) => (!selectedRowKeys.includes(item.id) ? item : null))
        .filter((key) => !!key) as CrmReceiverCustomer[]) || [],
    );
    setSelectedRowKeys(
      (state) =>
        (props.dataSource
          ?.map((item) => (!state.includes(item.id) ? item.id : null))
          .filter((key) => !!key) as number[]) || [],
    );
    setSelectAll(false);
    setInvertSelection(true);
    setNotSentRecent(false);
  }

  function handleNotSentRecent() {
    onCustomersSelected(
      props.dataSource?.filter((item) => !item.customer?.lastSendedEmail) || [],
    );
    setSelectedRowKeys(
      props.dataSource
        ?.filter((item) => !item.customer?.lastSendedEmail)
        ?.map((item) => item.id) || [],
    );
    setSelectAll(false);
    setInvertSelection(false);
    setNotSentRecent(true);
  }

  const handleSetEmail = (row: CrmReceiverCustomer, value: string) => {
    setSelectedRowKeys((prev) =>
      prev.some((key) => key === row.id) ? prev : [...prev, row.id],
    );
    setSelectedRowKeysItens((prev) => [
      ...prev,
      { id: row.id, itensIds: row.itens?.map(({ id }) => id) || [] },
    ]);

    onCustomersSelected((prev) => {
      const dataItem = prev.find((item) => item?.id === row.id);

      if (dataItem) {
        return [
          ...prev.filter((item) => item?.id !== row.id),
          {
            ...dataItem,
            customer: {
              ...dataItem.customer,
              email: value,
            },
            vehicle: dataItem.vehicle,
            itens: dataItem.itens,
          },
        ];
      }

      return [
        ...prev,
        {
          ...row,
          customer: { ...row.customer, email: value },
        },
      ];
    });
  };

  let columns: ColumnsType<CrmReceiverCustomer> = [
    {
      dataIndex: ['customer', 'name'],
      title: 'Cliente',
      width: 150,
      ellipsis: true,
    },
    {
      dataIndex: ['customer', 'email'],
      title: 'E-mail',
      width: 130,
      render: (value, row) => (
        <>
          <Form.Item
            initialValue={value}
            style={{ margin: 0 }}
            name={[row.id, 'customer', 'email']}
            getValueFromEvent={({ target }) => {
              handleSetEmail(row, target.value);

              return target.value;
            }}
            rules={[{ type: 'email', message: '' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            hidden
            name={[row.id, 'customer', 'id']}
            initialValue={row.id}
          />
        </>
      ),
    },
    shippingType === SendOptionType.birthdays
      ? {
          dataIndex: ['customer', 'date'],
          title: 'Aniversário',
          width: 150,
          render: (date) => (date ? moment(date).format('DD MMM') : '-'),
        }
      : {},
    {
      dataIndex: ['customer', 'lastSendedEmail'],
      title: 'Último envio',
      width: 150,
      render: (date) => (date ? moment(date).format('DD/MM/YYYY') : '-'),
    },
  ];

  if (shippingType === SendOptionType.recoveryOfDebts) {
    columns = [
      {
        dataIndex: ['debit', 'code'],
        title: 'Código',
        width: 100,
        render: (value: string) => value || '-',
      },
      {
        dataIndex: ['debit', 'situation'],
        title: 'Situação',
        width: 100,
        render: (situation: AccountReceivableSituation) => (
          <StatusTag
            color={AccountReceivableSituationColor[situation]}
            label={AccountRecivableSituationType[situation]}
          />
        ),
      },
      {
        dataIndex: ['debit', 'dueDate'],
        title: 'Vencimento',
        width: 100,
        render: (date: string) =>
          date ? moment(date).format('DD/MM/YYYY') : '-',
      },
      ...columns,
    ];
  } else if (shippingType === SendOptionType.futureServices) {
    columns = [
      {
        title: 'Serviços futuros',
        width: 100,
        render: (_, row) => `${row.futureServices?.length || 0} serviços`,
      },
      {
        dataIndex: ['vehicle', 'licensePlate'],
        title: 'Placa',
        width: 100,
        render: (value: string) => value || '-',
      },
      ...columns,
    ];
  } else if (shippingType === SendOptionType.nextReviews) {
    columns = [
      {
        title: 'Peças',
        width: 100,
        render: (_, row) => {
          if (!row?.itens || row.itens.length === 0) {
            return '0 peças/serviços';
          }

          let countParts = 0;
          let countServices = 0;

          row.itens.forEach((item) => {
            if (item.type === RevisionItemType.Part) {
              countParts++;
            } else if (item.type === RevisionItemType.Service) {
              countServices++;
            }
          });

          if (countParts > 0 && countServices > 0) {
            return `${row.itens.length} peças/serviços`;
          } else if (countParts > 0) {
            return countParts === 1 ? '1 peça' : `${countParts} peças`;
          } else if (countServices > 0) {
            return countServices === 1
              ? '1 serviço'
              : `${countServices} serviços`;
          } else {
            return `${row.itens.length} itens`;
          }
        },
      },
      {
        dataIndex: ['vehicle', 'licensePlate'],
        title: 'Placa',
        width: 100,
        render: (value: string) => value || '-',
      },
      ...columns,
    ];
  } else if (withVehicles.includes(shippingType)) {
    columns = [
      {
        dataIndex: ['vehicle', 'brand'],
        title: 'Marca',
        width: 100,
        render: (value: string) => value || '-',
      },
      {
        dataIndex: ['vehicle', 'model'],
        title: 'Modelo',
        width: 100,
        render: (value: string) => value || '-',
      },
      {
        dataIndex: ['vehicle', 'licensePlate'],
        title: 'Placa',
        width: 100,
        render: (value: string) => value || '-',
      },
      ...columns,
    ];
  }

  function renderFutureServicesExpandable():
    | ExpandableConfig<CrmReceiverCustomer>
    | undefined {
    if (shippingType === SendOptionType.futureServices) {
      return {
        columnWidth: 20,
        rowExpandable: (row) => !!row.futureServices?.length,
        expandedRowRender: (row) => (
          <>
            <Table
              className="table-expanded"
              pagination={false}
              dataSource={row.futureServices}
              scroll={{ x: true }}
              columns={[
                {
                  dataIndex: 'description',
                  title: 'Descrição',
                  width: 100,
                },
                {
                  dataIndex: 'date',
                  title: 'Data',
                  width: 100,
                  render: (date) =>
                    date ? moment(date).format('DD/MM/YYYY') : '-',
                },
              ]}
            />
          </>
        ),
      };
    }

    return undefined;
  }

  const findEmailForSelectedRow = (id: number) => {
    const customers = Object.values(form.getFieldsValue()).filter(
      (value) => typeof value !== 'boolean',
    ) as Customer[];

    const email = customers.find(({ customer }) => customer?.id === id)
      ?.customer.email;

    return email;
  };

  function renderNextRevisionsExpandable():
    | ExpandableConfig<CrmReceiverCustomer>
    | undefined {
    if (shippingType === SendOptionType.nextReviews) {
      return {
        columnWidth: 20,
        rowExpandable: (row) => !!row.itens?.length,
        expandedRowRender: (row) => (
          <>
            <Table
              className="table-expanded"
              pagination={false}
              dataSource={row.itens}
              rowKey={(row) => row.id}
              scroll={{ x: true }}
              rowSelection={{
                preserveSelectedRowKeys: true,
                selectedRowKeys:
                  selectedRowKeysItens.find(({ id }) => id === row.id)
                    ?.itensIds || [],
                hideSelectAll: true,
                onChange: (keys) => {
                  setSelectedRowKeysItens((prev) => [
                    ...prev.filter((item) => item.id !== row.id),
                    { id: row.id, itensIds: keys as number[] },
                  ]);

                  const combinedData = [
                    ...(props.dataSource || []).map(
                      ({ id, customer, vehicle, itens }) => ({
                        id,
                        customer,
                        vehicle,
                        itens,
                      }),
                    ),
                    ...CustomersSelected,
                  ];

                  const findItens = combinedData
                    .reduce((acc: CrmReceiverCustomer[], cur) => {
                      return acc.some(({ id }) => cur.id === id)
                        ? acc
                        : [...acc, cur];
                    }, [])
                    .find(({ id }) => id === row.id)
                    ?.itens?.filter((item) => keys.includes(item.id));

                  onCustomersSelected((prev) =>
                    findItens?.length
                      ? [
                          ...prev.filter((item) => item.id !== row.id),
                          {
                            ...row,
                            itens: findItens,
                            customer: {
                              ...row.customer,
                              email: findEmailForSelectedRow(row.id),
                            },
                          },
                        ]
                      : prev.filter(({ id }) => id !== row.id),
                  );

                  setSelectedRowKeys((prev) =>
                    findItens?.length
                      ? [...prev, row.id]
                      : prev.filter((id) => id !== row.id),
                  );
                },
              }}
              columns={[
                {
                  dataIndex: 'type',
                  title: 'Tipo',
                  width: 100,
                  render: (_, item) =>
                    item.type ? (
                      <StatusTag
                        color={workorderTypeColor[item.type]}
                        label={workorderTypeItemSmall[item.type]}
                      />
                    ) : (
                      '-'
                    ),
                },
                {
                  dataIndex: 'name',
                  title: 'Descrição',
                  width: 100,
                },
                {
                  dataIndex: 'revisionDateKm',
                  title: 'Data de revisão (Km)',
                  width: 100,
                  render: (date) =>
                    date ? moment(date).format('DD/MM/YYYY') : '-',
                },
                {
                  dataIndex: 'revisionDateLifespan',
                  title: 'Data de revisão (Tempo de vida)',
                  width: 100,
                  render: (date) =>
                    date ? moment(date).format('DD/MM/YYYY') : '-',
                },
              ]}
            />
          </>
        ),
      };
    }

    return undefined;
  }

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
        <Col span={isMobile ? 24 : undefined}>
          <Popover
            title="Versão Beta"
            content="Selecionará apenas os clientes listados"
          >
            <Button
              type={selectAll ? 'primary' : undefined}
              onClick={handleSelectAll}
              block
            >
              Selecionar todos
            </Button>
          </Popover>
        </Col>

        <Col span={isMobile ? 24 : undefined}>
          <Popover
            title="Versão Beta"
            content="Selecionará apenas os clientes listados"
          >
            <Button
              type={notSentRecent ? 'primary' : undefined}
              onClick={handleNotSentRecent}
              block
            >
              Não enviado recentemente
            </Button>
          </Popover>
        </Col>

        <Col span={isMobile ? 24 : undefined}>
          <Button block onClick={handleInvertSelection}>
            Inverter seleção
          </Button>
        </Col>

        <Col span={isMobile ? 24 : undefined}>
          <Button block onClick={handleRemoveSelection}>
            Desmarcar todos
          </Button>
        </Col>
      </Row>

      <InfiniteTable
        columns={columns.filter((item) => !!item?.title)}
        {...props}
        rowKey={(row) => row.id}
        expandable={
          renderFutureServicesExpandable() || renderNextRevisionsExpandable()
        }
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys,
          hideSelectAll: true,
          getCheckboxProps: (row) => {
            const selectedItens =
              selectedRowKeysItens.find(({ id }) => row.id === id)?.itensIds ||
              [];
            const totalItens = row.itens?.map((item) => item.id) || [];

            return {
              indeterminate:
                selectedItens.length > 0 &&
                selectedItens.length < totalItens.length,
            };
          },
          onSelect: (row, selected, selectedRows) => {
            setSelectedRowKeys(selectedRows.map(({ id }) => id));

            if (selected) {
              setSelectedRowKeysItens((prev) => [
                ...prev,
                {
                  id: row.id,
                  itensIds: row.itens?.map((item) => item.id) || [],
                },
              ]);
              onCustomersSelected((prev) => [
                ...prev,
                {
                  ...row,
                  customer: {
                    ...row.customer,
                    email: findEmailForSelectedRow(row.id),
                  },
                  vehicle: row.vehicle,
                },
              ]);
            } else {
              setSelectedRowKeys((prev) => [
                ...prev.filter((key) => key !== row.id),
              ]);
              setSelectedRowKeysItens((prev) => [
                ...prev.filter(({ id }) => id !== row.id),
              ]);
              onCustomersSelected((prev) => [
                ...prev.filter(({ id }) => id !== row.id),
              ]);
            }
          },
        }}
      />
    </>
  );
}

export default ShippingOptionsCustomerTable;
